import { useEffect, useRef } from "react";
import AuditEventCard from "./AuditEventCard";
import AuditRawEvent from "./AuditRawEvent";

const SESSION_GAP_MS = 2 * 60 * 1000;

const dayLabel = (date) => {
  const day = new Date(date);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (day.toDateString() === today.toDateString()) return "Aujourd'hui";
  if (day.toDateString() === yesterday.toDateString()) return "Hier";
  return day.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: day.getFullYear() === today.getFullYear() ? undefined : "numeric" });
};

const sameActor = (a, b) => a.kind === b.kind && String(a.id || "") === String(b.id || "");
const continuesSession = (newer, older) =>
  newer.action === "UPDATE" &&
  older.action === "UPDATE" &&
  String(newer.documentId) === String(older.documentId) &&
  sameActor(newer.actor, older.actor) &&
  new Date(newer.createdAt) - new Date(older.createdAt) <= SESSION_GAP_MS;

const isBlank = (value) => value === null || value === undefined || value === "";
const isSetChange = (change) => Array.isArray(change.from) && Array.isArray(change.to);
const isScalarChange = (change) => !Array.isArray(change.from) && !Array.isArray(change.to);
const isNetZero = (change) => (isBlank(change.from) && isBlank(change.to)) || JSON.stringify(change.from ?? null) === JSON.stringify(change.to ?? null);
const valueKey = (value) => (value !== null && typeof value === "object" ? JSON.stringify(value) : String(value));
const without = (items, excluded) => {
  const excludedKeys = new Set(excluded.map(valueKey));
  return items.filter((item) => !excludedKeys.has(valueKey(item)));
};
const dedupe = (items) => [...new Map(items.map((item) => [valueKey(item), item])).values()];
const carriesDocument = (change) => Boolean(change.fromUrl || change.toUrl || change.fromUrls || change.toUrls);
const sameShape = (a, b) => a.path === b.path && (a.subject ?? "") === (b.subject ?? "") && isSetChange(a) === isSetChange(b) && isScalarChange(a) === isScalarChange(b);

const mergeChanges = (saves) => {
  const merged = [];
  for (const save of saves) {
    for (const change of save.changes || []) {
      const earlier =
        (isScalarChange(change) || isSetChange(change)) && !carriesDocument(change) && merged.find((candidate) => sameShape(candidate, change) && !carriesDocument(candidate));
      if (!earlier) {
        merged.push({ ...change });
        continue;
      }
      if (isSetChange(change)) {
        const added = dedupe([...without(earlier.to, change.from), ...without(change.to, earlier.from)]);
        const removed = dedupe([...without(earlier.from, change.to), ...without(change.from, earlier.to)]);
        earlier.to = added;
        earlier.from = removed;
        continue;
      }
      earlier.to = change.to;
      earlier.toLabel = change.toLabel;
    }
  }
  return merged.filter((change) => (isSetChange(change) ? change.from.length > 0 || change.to.length > 0 : !isScalarChange(change) || !isNetZero(change)));
};

const groupSessions = (events) => {
  const sessions = [];
  for (const event of events) {
    const current = sessions[sessions.length - 1];
    if (current && continuesSession(current[current.length - 1], event)) current.push(event);
    else sessions.push([event]);
  }
  return sessions.map((saves) => {
    if (saves.length === 1) return saves[0];
    const chronological = [...saves].reverse();
    return { ...saves[0], changes: mergeChanges(chronological), saves: chronological };
  });
};

const AuditTimeline = ({ events, raw, showSubject, hasNextPage, isFetchingNextPage, onLoadMore }) => {
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!hasNextPage || !sentinelRef.current) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !isFetchingNextPage) onLoadMore();
    });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, onLoadMore]);

  const days = [];
  for (const event of raw ? events : groupSessions(events)) {
    const key = new Date(event.createdAt).toDateString();
    if (days[days.length - 1]?.key !== key) days.push({ key, date: event.createdAt, events: [] });
    days[days.length - 1].events.push(event);
  }

  return (
    <div className="flex flex-col gap-4">
      {days.map((day) => (
        <div key={day.key} className="flex flex-col gap-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{dayLabel(day.date)}</div>
          {day.events.map((event) => (raw ? <AuditRawEvent key={event._id} event={event} /> : <AuditEventCard key={event._id} event={event} showSubject={showSubject(event)} />))}
        </div>
      ))}
      <div ref={sentinelRef} className="h-px" />
      {isFetchingNextPage && <div className="py-2 text-center text-xs text-gray-400">Chargement...</div>}
    </div>
  );
};

export default AuditTimeline;
